import type {
  IndividualPaymentOrderTrackingViewModel,
  IndividualPaymentTraceabilityViewModel,
} from '@/shared/api/generated';
import {
  useGetApiV1IndividualpaymentParcelsParcelidOrderTracking,
  useGetApiV1IndividualpaymentParcelsParcelidTraceability,
} from '@/shared/api/generated';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

import type { PaymentHistoryMode, PaymentHistoryResult } from '../types/paymentHistory';

/**
 * Histórico de uma parcela — rastreabilidade de alterações ou acompanhamento do
 * pedido, conforme o modo.
 *
 * As duas queries vivem no mesmo hook porque alimentam o mesmo painel e nunca
 * são exibidas ao mesmo tempo: `enabled` liga só a do modo ativo, então abrir o
 * painel dispara uma requisição, não duas.
 *
 * @param parcelId - ID da parcela; `null` desabilita as duas queries.
 * @param mode - Histórico a carregar; `null` quando o painel está fechado.
 *
 * @example
 * ```tsx
 * const { traceability, orderTracking, isLoading } = useParcelHistory(parcelId, 'traceability');
 * ```
 */
export function useParcelHistory(
  parcelId: number | null,
  mode: PaymentHistoryMode | null,
): PaymentHistoryResult {
  // Segundo argumento é `params` (a rastreabilidade aceita `isBilletHistory`,
  // que alterna para o histórico do boleto — não usado aqui); as opções de query
  // vão no terceiro.
  const traceabilityQuery = useGetApiV1IndividualpaymentParcelsParcelidTraceability(
    parcelId ?? 0,
    undefined,
    { query: { enabled: parcelId !== null && mode === 'traceability' } },
  );

  const orderTrackingQuery = useGetApiV1IndividualpaymentParcelsParcelidOrderTracking(
    parcelId ?? 0,
    undefined,
    { query: { enabled: parcelId !== null && mode === 'orderTracking' } },
  );

  const active = mode === 'orderTracking' ? orderTrackingQuery : traceabilityQuery;

  return {
    traceability: unwrapPagedResponse<IndividualPaymentTraceabilityViewModel>(
      traceabilityQuery.data,
    ),
    orderTracking: unwrapPagedResponse<IndividualPaymentOrderTrackingViewModel>(
      orderTrackingQuery.data,
    ),
    isLoading: mode !== null && active.isLoading,
    isError: active.isError,
    refetch: active.refetch,
  } as const;
}
